import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Power, Building, UserCircle, UserCircle2, Mail, Tag, Search, Edit3, Trash2, Check, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, EyeOff, RefreshCw, Users as UsersIcon, ChevronUp, ChevronDown, Columns3, Loader2, CircleEllipsis, Pencil } from 'lucide-react';
import { Modal } from "@mui/material";
import { hasPermission } from '../utils/permissionsHelper';
import { jwt_decode } from '../utils/jwt_decode';
import baseapi from '../services/baseapi';
import { showError, showSuccess } from '../utils/toast';
import isEqual from 'lodash/isEqual';
import { useClientLevel } from '../hooks/useClientLevel';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import { ItemsPerPageDropdown } from '../components/ItemsPerPageDropdown ';


interface User {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    enabled: boolean;
    attributes: {
        client_id?: (string | { id: number | string; value: string })[];
        tenant_name?: string[];
        role?: string[];
        organization_id?: string[];
    };
    client_id?: string | number;
    role: string | null;
}
interface Role {
    id: string;
    client_id: string;
    composite: boolean;
    description: string;
    is_deleted: boolean;
    organization_id: string;
    permissions: Record<string, string>;
    role: string;
}

interface SearchableDropdownProps {
    roles: Role[];
    value: string;
    onChange: (value: string) => void;
    error?: string;
    disabled?: boolean;
}

const SearchableRoleDropdown = ({ roles, value, onChange, error, disabled }: SearchableDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Filter roles based on search term
    const filteredRoles = roles.filter((role) =>
        role.role?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get selected role display name
    const selectedRole = roles.find((role) => role.role === value);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus input when dropdown opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const handleSelect = (roleName: string) => {
        onChange(roleName);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div>
            <label className={`block text-sm font-semibold mb-1 flex items-center gap-2 ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                <Tag size={18} className={disabled ? 'text-gray-400 dark:text-gray-600' : 'text-orange-500 dark:text-orange-400'} />
                Role <span className="text-red-500 dark:text-red-400 text-base">*</span>
            </label>

            <div className="relative" ref={dropdownRef}>
                {/* Dropdown trigger */}
                <button
                    type="button"
                    onClick={handleToggle}
                    disabled={disabled}
                    className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/80 border rounded-lg text-gray-900 dark:text-white text-sm flex items-center justify-between ${error ? 'border-red-500 bg-red-50 dark:bg-red-500/5' : 'border-gray-300 dark:border-gray-600'
                        } ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'hover:border-gray-400 dark:hover:border-gray-500'} transition-colors`}
                >
                    <span className={selectedRole && !disabled ? 'text-gray-900 dark:text-white' : 'text-gray-400'}>
                        {selectedRole ? selectedRole.role : (disabled ? 'Select a client first' : 'Select a role')}
                    </span>
                    <ChevronDown
                        size={18}
                        className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                </button>

                {/* Dropdown menu */}
                {isOpen && (
                    <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-hidden">
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    placeholder="Search roles..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Options list */}
                        <div className="overflow-y-auto max-h-48">
                            {filteredRoles.length > 0 ? (
                                filteredRoles.map((role) => (
                                    <button
                                        key={role.id}
                                        type="button"
                                        onClick={() => handleSelect(role.role)}
                                        className={`w-full px-4 py-2.5 text-left text-sm transition-colors cursor-pointer ${value === role.role
                                            ? 'bg-orange-600 text-white font-medium'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                            }`}
                                    >
                                        {role.role}
                                    </button>
                                ))
                            ) : (
                                <div className="px-4 py-8 text-center text-gray-400 text-sm">
                                    No roles found
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Error message */}
            {error && (
                <div className="flex items-center gap-2 mt-1 text-red-500 dark:text-red-400 text-sm">
                    <AlertCircle size={14} />
                    {error}
                </div>
            )}
        </div>
    );
};


const Users = () => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [editingUser, setEditingUser] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [users, setUsers] = useState<User[]>([]);
    const [deleteUser, setDeleteUser] = useState<User | null>(null);
    const [originalFormData, setOriginalFormData] = useState<any>(null);
    const [open, setOpen] = useState(false);
    const [roles, setRoles] = useState<Role[]>([]);

    // Actions dropdown menu state
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: string | number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({ top: rect.bottom + 4, left: Math.max(10, rect.left - 110) });
        setOpenMenuId(prev => (prev === String(id) ? null : String(id)));
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (openMenuId && !target.closest(`[data-menu-id="${openMenuId}"]`) && !target.closest('[data-action-menu]')) {
                setOpenMenuId(null);
            }
        };
        const handleScroll = () => {
            if (openMenuId) setOpenMenuId(null);
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [openMenuId]);

    // eslint-disable-next-line
    const jwt = jwt_decode();
    const organization_id = jwt.organization_id;
    const tenant_name = jwt.tenant_id || jwt.tenant_name; // fallback if needed

    // Pagination states
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const isFirstSearch = useRef(true);

    //permissions
    const canCreate = hasPermission('users', 'create')
    const canUpdate = hasPermission('users', 'update')
    const canDelete = hasPermission('users', 'delete')

    // Column visibility state
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    type ColumnKey = 'first_name' | 'last_name' | 'email' | 'client' | 'role' | 'status' | 'actions';
    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
        first_name: true,
        last_name: true,
        email: true,
        client: true,
        role: true,
        status: true,
        actions: canUpdate || canDelete
    });

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        enabled: true,
        role: ''
    });

    const level = useClientLevel();
    const [clients, setClients] = useState<{
        id: any; name: string; display_name: string
    }[]>([]);
    const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);

    useEffect(() => {
        if (level?.name === 'global') {
            baseapi.get('/clients', { params: { limit: 1000, skip: 0 } })
                .then(res => {
                    if (res.data.status) {
                        setClients(res.data.data);
                    }
                })
                .catch(() => { });
        }
    }, [level]);

    useEffect(() => {
        fetchData(0, itemsPerPage);
    }, [level]);

    // Fetch roles ONLY when clients change AND at least one client is selected
    useEffect(() => {
        if (level?.name === 'global') {
            if (selectedClientIds.length > 0) {
                fetchRoles();
            } else {
                setRoles([]); // Clear roles if no client selected
            }
        } else if (level?.name) {
            // At a specific tenant/client level, fetch directly
            fetchRoles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClientIds, level]);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);

    // Re-fetch when debouncedSearch changes
    useEffect(() => {
        if (isFirstSearch.current) {
            isFirstSearch.current = false;
            return;
        }
        fetchData(0, itemsPerPage);
    }, [debouncedSearch]);

    const fetchData = async (skip: number = 0, limit: number = 5, orderBy: Record<string, 'asc' | 'desc'>[] = [{ created: 'desc' }]) => {
        try {
            setLoading(true);
            const [sortEntry] = orderBy;
            const sort_by = Object.keys(sortEntry)[0];
            const sort_order = sortEntry[sort_by];

            let params: any = {
                skip: skip,
                limit: limit,
                sort_by,
                sort_order
            }

            if (debouncedSearch) {
                params.query = debouncedSearch;
            }
            if (level?.name !== 'global') {
                params.client_id = level?.id || '';
            }

            const response = await baseapi.get("/users", { params });
            if (response.data.status) {
                setUsers(response.data.data);
                setTotalItems(response.data.total);
                setTotalPages(Math.ceil(response.data.total / limit));
                const maxPage = Math.ceil(response.data.total / limit) || 1;
                if (currentPage > maxPage) {
                    setCurrentPage(maxPage);
                }
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'An error occurred';
            showError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        let params: any = {};

        // Check if we are at the global level
        if (level?.name === "global") {
            const isAllSelected = selectedClientIds.includes('*') || (clients.length > 0 && selectedClientIds.length === clients.length);
            if (isAllSelected) {
                // All clients selected: pass is_global: true
                params = { query: JSON.stringify({ is_global: true }) };
            } else if (selectedClientIds.length === 1) {
                // Only one client selected: pass the specific client ID
                params = { query: JSON.stringify({ client_id: Number(selectedClientIds[0]) }) };
            } else if (selectedClientIds.length > 1) {
                // Multiple clients selected: pass is_global: true
                params = { query: JSON.stringify({ is_global: true }) };
            } else {
                return; // Guard: do nothing if 0 clients
            }
        } else {
            // Not global level: Use the current level's ID
            params = { query: JSON.stringify({ client_id: level?.id ? Number(level.id) : undefined }) };
        }

        try {
            const response = await baseapi.get("/roles", { params });
            if (response.data.status) {
                setRoles(response.data.data);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            showError(error?.response?.data?.message || error.message);
        }
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                const newDirection = prev.direction === 'asc' ? 'desc' : 'asc';
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: newDirection }]);
                return { key, direction: newDirection };
            } else {
                // First interaction with a column - start with 'asc'
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: 'asc' }]);
                return { key, direction: 'asc' };
            }
        });
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        // Basic info validation
        if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
        if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
        if (!formData.role) newErrors.role = 'Role is required';
        if (level?.name === 'global' && selectedClientIds.length === 0) newErrors.client = 'Client is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleStatusChange = (enabled: boolean) => {
        setFormData(prev => ({ ...prev, enabled }));
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            const isAllClientsSelected = level?.name === 'global' && (
                selectedClientIds.includes('*') ||
                (clients.length > 0 && selectedClientIds.length === clients.length)
            );

            const clientIdToUse = level?.name === 'global'
                ? (isAllClientsSelected ? null : selectedClientIds)
                : [level?.id];

            const params: any = {
                email: formData.email,
                first_name: formData.first_name,
                last_name: formData.last_name,
                enabled: formData.enabled,
                attributes: {
                    client_id: isAllClientsSelected ? null : clientIdToUse,
                    organization_id: organization_id,
                    tenant_name: tenant_name,
                    role: formData.role
                },
                role: formData.role
            };

            if (level?.name === 'global') {
                params.client_ids = isAllClientsSelected ? null : selectedClientIds;
                params.client_id = isAllClientsSelected ? null : (selectedClientIds.length === 1 ? selectedClientIds[0] : null);
            }

            let response;
            if (editingUser) {
                response = await baseapi.put(`/users/${editingUser}`, params);
            } else {
                response = await baseapi.post('/users', params);
            }

            if (response.data.status) {
                showSuccess(response.data.message);
                resetForm();
                setShowAddForm(false);
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'An error occurred';
            showError(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            enabled: true,
            role: ''
        });
        setEditingUser(null);
        setShowAddForm(false);
        setErrors({});
        setSelectedClientIds([]);
    };


    const handleEdit = (user: User) => {
        const editData = {
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            enabled: user.enabled,
            role: user.role || user.attributes?.role?.[0] || ''
        };
        setFormData(editData);
        setOriginalFormData(editData);
        setEditingUser(user.id);
        setShowAddForm(true);
        // Set the client dropdown to the user's client (for global level)
        if (level?.name === 'global') {
            const clientAttr = user.attributes?.client_id;
            let cid: string[] = [];
            if (!clientAttr || clientAttr.length === 0 || clientAttr[0] === null) {
                if (user.client_id) {
                    cid = [String(user.client_id)];
                } else {
                    cid = ['*'];
                }
            } else {
                cid = clientAttr.map(c => typeof c === 'object' ? String(c.id || (c as any).value) : String(c));
            }
            setSelectedClientIds(cid);
            setOriginalFormData((prev: any) => ({ ...prev, client_id: cid }));
        }
    };

    const isFormChanged = editingUser ?
        (!isEqual(formData, originalFormData) || !isEqual(selectedClientIds, originalFormData.client_id))
        : true;

    const handleDelete = async (userId: string) => {
        try {
            setLoading(true);

            // Optimistically update users
            const updatedUsers = users.filter(user => user.id !== userId);
            setUsers(updatedUsers);

            const response = await baseapi.delete(`/users/${userId}`);

            if (response.data.status) {
                showSuccess(response.data.message);
                // Fetch fresh data to ensure consistency
                await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
                // Adjust currentPage if necessary
                const newTotalPages = Math.ceil((totalItems - 1) / itemsPerPage) || 1;
                if (currentPage > newTotalPages) {
                    setCurrentPage(newTotalPages);
                }
            } else {
                showError(response.data.message || 'Failed to delete user');
                await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'An error occurred';
            showError(errorMsg);
            await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        setIsRefreshing(true);
        setCurrentPage(1);
        setSearchTerm('');
        setDebouncedSearch('');
        fetchData();
        setTimeout(() => {
            setIsRefreshing(false);
        }, 1000);
    };

    // Pagination functions
    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchData((itemsPerPage * (page - 1)), itemsPerPage);
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
        fetchData(0, value);
    };

    // Column visibility functions
    const handleColumnToggle = (column: ColumnKey) => {
        setVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    };

    const columnOptions: { key: ColumnKey; label: string }[] = [
        { key: 'first_name', label: 'First Name' },
        { key: 'last_name', label: 'Last Name' },
        { key: 'email', label: 'Email' },
        { key: 'client', label: 'Clients' },
        { key: 'role', label: 'Role' },
        { key: 'status', label: 'Status' },
        ...(canUpdate || canDelete
            ? [{ key: 'actions' as ColumnKey, label: 'Actions' }]
            : []),
    ];

    return (
        <div className="bg-gray-50 dark:bg-gray-900 p-3 min-h-screen pb-20 transition-colors duration-200">
            <div className="w-full mx-auto">
                {/* Top Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-1.5">
                        <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                            <UsersIcon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h1 className="font-bold text-lg text-gray-900 dark:text-gray-200 leading-none">Users</h1>
                        </div>
                        {!loading &&
                            <div className='font-bold text-lg text-gray-500 dark:text-gray-100'>
                                ({totalItems})
                            </div>
                        }
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleRefresh}
                            className="rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                            title="Refresh"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>

                        <div className="relative w-full sm:w-56">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                className="w-full bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500 outline-none transition-all"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>

                        <div className="relative" data-col-menu>
                            <button
                                onClick={() => setShowColumnFilter(!showColumnFilter)}
                                className="p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-all flex items-center gap-2 cursor-pointer shadow-sm"
                            >
                                <Columns3 className="w-3.5 h-3.5" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                            </button>
                            {showColumnFilter && (
                                <div className="absolute top-full mt-2 right-0 w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto">
                                    <div className="px-3 py-1.5 text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-100 dark:border-gray-800 mb-1">Toggle Columns</div>
                                    {columnOptions.map((option) => (
                                        <button
                                            key={option.key}
                                            onClick={() => handleColumnToggle(option.key)}
                                            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200 rounded-lg transition-colors cursor-pointer"
                                        >
                                            <div className="flex items-center gap-2.5">
                                                {visibleColumns[option.key] ? (
                                                    <Eye className="w-3 h-3 text-orange-600 dark:text-orange-500" />
                                                ) : (
                                                    <EyeOff className="w-3 h-3 text-gray-400 dark:text-gray-600" />
                                                )}
                                                {option.label}
                                            </div>
                                            <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${visibleColumns[option.key] ? 'bg-orange-600 border-orange-600' : 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'}`}>
                                                {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {canCreate && (
                            <button
                                onClick={() => {
                                    if (showAddForm) {
                                        resetForm();
                                    } else {
                                        setShowAddForm(true);
                                    }
                                }}
                                className="flex items-center gap-1.5 bg-orange-600 rounded-lg px-4 py-1.5 text-[11px] text-white font-bold hover:bg-orange-700 shadow-lg shadow-orange-900/20 transition-all cursor-pointer"
                            >
                                <Plus className="w-4 h-4" />
                                Add User
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-t-xl overflow-hidden shadow-sm dark:shadow-2xl">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-200 dark:border-gray-700/50">
                                    {columnOptions.map(({ key, label }) => (
                                        visibleColumns[key] && (
                                            <th
                                                key={key}
                                                onClick={() => key !== 'actions' && handleSort(key)}
                                                className={`px-2 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest select-none whitespace-nowrap ${key === 'actions' ? 'text-center' : 'text-left cursor-pointer hover:text-orange-600 dark:hover:text-orange-400 transition-colors'}`}
                                            >
                                                <div className="flex items-center gap-1">
                                                    {label}
                                                    {key !== 'actions' && (
                                                        sortConfig?.key === key ? (
                                                            sortConfig.direction === 'asc' ? (
                                                                <ChevronUp size={12} />
                                                            ) : (
                                                                <ChevronDown size={12} />
                                                            )
                                                        ) : (
                                                            <ChevronDown size={12} className="opacity-40" />
                                                        )
                                                    )}
                                                </div>
                                            </th>
                                        )
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30 bg-white dark:bg-transparent">
                                {loading ? (
                                    <tr>
                                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-gray-600 dark:text-gray-200 font-bold uppercase tracking-widest italic">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                                                Loading users
                                            </div>
                                        </td>
                                    </tr>
                                ) : users.length === 0 ? (
                                    <tr>
                                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest italic">
                                            No users found
                                        </td>
                                    </tr>
                                ) : (
                                    users.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-200 dark:border-gray-700/30">
                                            {visibleColumns.first_name && (
                                                <td className="px-2 py-1.5"><span className="text-[11px] text-gray-900 dark:text-gray-200 font-medium">{user.first_name}</span></td>
                                            )}
                                            {visibleColumns.last_name && (
                                                <td className="px-2 py-1.5"><span className="text-[11px] text-gray-900 dark:text-gray-200 font-medium">{user.last_name}</span></td>
                                            )}
                                            {visibleColumns.email && (
                                                <td className="px-2 py-1.5"><span className="text-[11px] text-gray-700 dark:text-gray-200 font-mono">{user.email}</span></td>
                                            )}
                                            {visibleColumns.client && (
                                                <td className="px-2 py-1.5 max-w-[150px]">
                                                    <div className="flex flex-wrap gap-1">
                                                        {
                                                            user.attributes?.client_id && Array.isArray(user.attributes.client_id) && user.attributes.client_id.length > 0 && user.attributes.client_id[0] !== null ? (
                                                                user.attributes.client_id.map((c: any, i: number) => (
                                                                    <span key={i} className="text-[9px] bg-orange-50 dark:bg-slate-800/80 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30 px-1.5 py-0.5 rounded truncate font-medium">
                                                                        {typeof c === 'object' ? (c.value || c.name) : c}
                                                                    </span>
                                                                ))
                                                            ) : user.client_id ? (
                                                                <span className="text-[9px] bg-orange-50 dark:bg-slate-800/80 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30 px-1.5 py-0.5 rounded truncate font-medium">
                                                                    {user.client_id}
                                                                </span>
                                                            ) : (
                                                                <span className="text-[9px] bg-orange-50 dark:bg-slate-800/80 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30 px-1.5 py-0.5 rounded truncate font-medium">
                                                                    All Clients
                                                                </span>
                                                            )
                                                        }
                                                    </div>
                                                </td>
                                            )}
                                            {visibleColumns.role && (
                                                <td className="px-2 py-1.5">
                                                    {user.role || user.attributes?.role?.[0] ? (
                                                        <span className="text-[9px] font-mono bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600/50 text-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded uppercase font-medium">
                                                            {user.role || user.attributes?.role?.[0]}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400 dark:text-gray-500 font-mono">-</span>
                                                    )}
                                                </td>
                                            )}

                                            {visibleColumns.status && (
                                                <td className="px-2 py-1.5">
                                                    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${user.enabled ? 'border-green-200 dark:border-green-500/20 bg-green-50 dark:bg-green-500/10' : 'border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10'}`}>
                                                        <div className={`w-1.5 h-1.5 rounded-full ${user.enabled ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.4)]' : 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.4)]'}`} />
                                                        <span className={`text-[9px] font-bold uppercase tracking-wide ${user.enabled ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>{user.enabled ? 'Active' : 'Inactive'}</span>
                                                    </div>
                                                </td>
                                            )}

                                            {visibleColumns.actions && (
                                                <td className="px-2 py-1.5 text-center">
                                                    <button
                                                        data-menu-id={user.id}
                                                        onClick={(e) => handleMenuOpen(e, user.id)}
                                                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                                                    >
                                                        <CircleEllipsis className="w-4.5 h-4.5" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Table Footer */}
                <div className="bg-gray-50/80 dark:bg-gray-800/50 border rounded-b-xl border-gray-200 dark:border-gray-700 px-3 py-1.5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <ItemsPerPageDropdown
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        />
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">
                            <span className="text-gray-900 dark:text-gray-200">{startIndex + 1}</span> - <span className="text-gray-900 dark:text-gray-200">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"><ChevronsLeft className="w-4 h-4" /></button>
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"><ChevronLeft className="w-4 h-4" /></button>
                        <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-sm dark:shadow-lg dark:shadow-orange-900/20">{currentPage}</div>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"><ChevronRight className="w-4 h-4" /></button>
                        <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"><ChevronsRight className="w-4 h-4" /></button>
                    </div>
                </div>

                {/* Sliding Add/Edit Form */}
                <div
                    className={`fixed top-10 pb-25 right-0 sm:right-4 h-[calc(100vh-2rem)] w-full max-w-full sm:max-w-[480px] md:max-w-[520px] lg:max-w-[580px] transition-all duration-500 ease-in-out transform z-50 ${showAddForm ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
                        }`}
                >
                    <div className="h-full bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 rounded-2xl flex flex-col">
                        <div className="flex items-center justify-between px-4 py-3 sm:p-4 border-b border-gray-200 dark:border-gray-700/50 bg-gray-50/80 dark:bg-transparent">
                            <div className="flex items-center gap-3">
                                <div className="p-2 sm:p-3 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl shadow-lg">
                                    {editingUser ? <Edit3 className="text-white" size={20} /> : <Plus className="text-white" size={20} />}
                                </div>
                                <div>
                                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                                        {editingUser ? 'Edit User' : 'Add New User'}
                                    </h2>
                                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                                        {editingUser ? 'Update user information' : 'Fill in the details to create a new user'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={resetForm}
                                className="p-2 sm:p-3 text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl transition-colors cursor-pointer"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-white dark:bg-transparent custom-scroll">
                            <div className="space-y-4">

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                                        <UserCircle size={18} className="text-orange-500 dark:text-orange-400" />
                                        First Name <span className="text-red-500 dark:text-red-400 text-base">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.first_name}
                                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                                        placeholder="Enter first name..."
                                        className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/80 border rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm outline-none transition-colors ${errors.first_name ? 'border-red-500 bg-red-50 dark:bg-red-500/5' : 'border-gray-300 dark:border-gray-600 focus:border-orange-500 focus:bg-white dark:focus:bg-gray-700'
                                            }`}
                                    />
                                    {errors.first_name && (
                                        <div className="flex items-center gap-2 mt-1 text-red-500 dark:text-red-400 text-sm">
                                            <AlertCircle size={14} />
                                            {errors.first_name}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                                        <UserCircle2 size={18} className="text-orange-500 dark:text-orange-400" />
                                        Last Name <span className="text-red-500 dark:text-red-400 text-base">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.last_name}
                                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                                        placeholder="Enter last name..."
                                        className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/80 border rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm outline-none transition-colors ${errors.last_name ? 'border-red-500 bg-red-50 dark:bg-red-500/5' : 'border-gray-300 dark:border-gray-600 focus:border-orange-500 focus:bg-white dark:focus:bg-gray-700'
                                            }`}
                                    />
                                    {errors.last_name && (
                                        <div className="flex items-center gap-2 mt-1 text-red-500 dark:text-red-400 text-sm">
                                            <AlertCircle size={14} />
                                            {errors.last_name}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                                        <Mail size={18} className="text-orange-500 dark:text-orange-400" />
                                        Email <span className="text-red-500 dark:text-red-400 text-base">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        placeholder="Enter email..."
                                        className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/80 border rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm outline-none transition-colors ${errors.email ? 'border-red-500 bg-red-50 dark:bg-red-500/5' : 'border-gray-300 dark:border-gray-600 focus:border-orange-500 focus:bg-white dark:focus:bg-gray-700'
                                            }`}
                                    />
                                    {errors.email && (
                                        <div className="flex items-center gap-2 mt-1 text-red-500 dark:text-red-400 text-sm">
                                            <AlertCircle size={14} />
                                            {errors.email}
                                        </div>
                                    )}
                                </div>

                                {/* Client Dropdown - hidden at global level */}
                                {level?.name === 'global' && (
                                    <div className="space-y-4">
                                        <MultiSelectDropdown
                                            label="Client"
                                            icon={Building}
                                            variant="project"
                                            options={clients.map(client => ({
                                                label: client.display_name || client.name,
                                                value: String(client.id),
                                            }))}
                                            value={selectedClientIds}
                                            onChange={(newClientIds) => {
                                                setSelectedClientIds(newClientIds);
                                                if (formData.role) {
                                                    // Reset the role explicitly when user alters the client selection
                                                    setFormData(prev => ({ ...prev, role: "" }));
                                                }
                                            }}
                                            placeholder="Select clients"
                                            required
                                            error={errors.client}
                                            width="w-full"
                                            showSelectAll
                                        />
                                    </div>
                                )}

                                <SearchableRoleDropdown
                                    roles={roles}
                                    value={formData.role}
                                    onChange={(value) => handleInputChange('role', value)}
                                    error={errors.role}
                                    disabled={level?.name === 'global' && selectedClientIds.length === 0}
                                />

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                                        <Power size={18} className="text-orange-500 dark:text-orange-400" />
                                        Status
                                    </label>
                                    <div className="flex gap-4">
                                        <button
                                            type="button"
                                            onClick={() => handleStatusChange(true)}
                                            className={`inline-flex items-center justify-center px-4 py-2 text-xs font-semibold rounded-full transition-colors cursor-pointer ${formData.enabled
                                                ? 'bg-green-600 text-white shadow-sm'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-transparent'
                                                }`}
                                        >
                                            Active
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleStatusChange(false)}
                                            className={`inline-flex items-center justify-center px-4 py-2 text-xs font-semibold rounded-full transition-colors cursor-pointer ${!formData.enabled
                                                ? 'bg-red-600 text-white shadow-sm'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-transparent'
                                                }`}
                                        >
                                            Inactive
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700/50 bg-gray-50/80 dark:bg-transparent">
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting || (editingUser ? !isFormChanged : false)}
                                    className={`w-full sm:flex-1 flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-4 rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg text-sm cursor-pointer
                                         ${(submitting || (editingUser && !isFormChanged)) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {submitting ? (
                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <Check size={18} />
                                    )}
                                    {submitting ? 'Submitting...' : (editingUser ? 'Update User' : 'Create User')}
                                </button>

                                <button
                                    onClick={resetForm}
                                    className="w-full sm:w-auto sm:px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700/80 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-transparent rounded-xl transition-colors duration-200 font-medium text-sm cursor-pointer"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Backdrop */}
                <div
                    className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 z-40 ${showAddForm ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        }`}
                    onClick={resetForm}
                />
            </div>

            {showColumnFilter && (
                <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowColumnFilter(false)}
                />
            )}

            <Modal open={open} onClose={() => setOpen(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-[95%] sm:w-[500px] md:w-[550px] max-w-2xl bg-white dark:bg-gray-900/95 backdrop-blur-xl 
                border border-red-200 dark:border-red-400/20 shadow-2xl p-6 rounded-2xl text-gray-900 dark:text-white
                relative overflow-hidden">

                    {/* Subtle top border */}
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-400/40 to-transparent"></div>

                    {/* Header */}
                    <div className="flex items-center mb-4">
                        <div className="flex items-center justify-center mr-2 text-red-500 dark:text-red-400">
                            <Trash2 className='h-7 w-7'></Trash2>
                        </div>
                        <div className='flex items-center justify-center'>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Confirm Delete</h2>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="mb-4 bg-red-50/50 dark:bg-gray-800/30 p-4 rounded-lg border border-red-100 dark:border-gray-700/50">
                        <p className="text-gray-800 dark:text-white/90 mb-3 text-base">
                            Are you sure you want to delete user <span className="font-semibold text-orange-600 dark:text-orange-400">{deleteUser?.first_name} {deleteUser?.last_name}</span>?
                        </p>
                        <p className="text-red-600 dark:text-orange-400/70 text-sm">
                            This will permanently remove the user and all associated data from your account.
                        </p>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 justify-end">
                        <button
                            onClick={() => setOpen(false)}
                            className="px-6 py-2.5 border border-gray-300 dark:border-gray-600/50 text-gray-700 dark:text-gray-300 rounded-lg
                      hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white
                      transition-all duration-200 font-medium min-w-[100px] cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                if (deleteUser) {
                                    handleDelete(deleteUser.id);
                                    setOpen(false);
                                }
                            }}
                            className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold
        hover:shadow-lg hover:shadow-red-600/20
        transition-all duration-200 min-w-[100px] cursor-pointer"
                        >
                            Delete User
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Actions Portal Menu */}
            {openMenuId && createPortal(
                (() => {
                    const activeUser = users.find(u => String(u.id) === String(openMenuId));
                    if (!activeUser) return null;
                    return (
                        <div
                            data-action-menu
                            className="fixed w-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden py-1.5"
                            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                        >
                            {canUpdate && (
                                <button
                                    onClick={() => {
                                        handleEdit(activeUser);
                                        setOpenMenuId(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors cursor-pointer"
                                >
                                    <Pencil className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /> Edit User
                                </button>
                            )}
                            {canDelete && (
                                <button
                                    onClick={() => {
                                        setDeleteUser(activeUser);
                                        setOpen(true);
                                        setOpenMenuId(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-[11px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-3 font-bold cursor-pointer"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete User
                                </button>
                            )}
                        </div>
                    );
                })(),
                document.body
            )}
        </div>
    );
};
export default Users;